import React, { useState } from "react";
import { Link2, Truck } from "lucide-react";
import { toast } from "react-toastify";
import { fetchCustomerTrackingLink } from "../../api/OrderAPIs";
import { canMutateOrders } from "../../utils/roles";
import { useAuth } from "../../utils/auth";

const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the textarea copy when the browser blocks the clipboard.
    }
  }
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  }
  document.body.removeChild(textArea);
  return copied;
};

const CopyTrackingLinkButton = ({ orderId, compact = false }) => {
  const { authState } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!orderId || !canMutateOrders(authState.role)) return null;

  const copy = async () => {
    setBusy(true);
    try {
      const data = await fetchCustomerTrackingLink(orderId);
      const url = data?.url || data?.Url;
      if (!url) throw new Error("missing");
      const copied = await copyText(url);
      if (!copied) throw new Error("copy");
      toast.success("تم نسخ لينك التتبع");
    } catch {
      toast.error("تعذر نسخ لينك التتبع");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      disabled={busy}
      title="نسخ لينك متابعة الأوردر"
      className={
        compact
          ? "w-full bg-violet-700 text-white py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition hover:bg-violet-800 disabled:opacity-60"
          : "w-full bg-violet-700 text-white px-3 py-3 rounded-lg hover:bg-violet-800 transition text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-60"
      }
    >
      <Link2 size={compact ? 13 : 16} />
      {busy ? "بنسخ اللينك..." : "لينك التتبع"}
    </button>
  );
};

export const CopyShippingLinkButton = ({ shippingTrackLink, compact = false }) => {
  const { authState } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!canMutateOrders(authState.role)) return null;

  const copy = async () => {
    const url = String(shippingTrackLink || "").trim();
    if (!url) {
      toast.info("مفيش لينك شحن لسه. اللينك بيتسجل بعد إرسال الأوردر لـ Fincart");
      return;
    }
    setBusy(true);
    try {
      const copied = await copyText(url);
      if (!copied) throw new Error("copy");
      toast.success("تم نسخ لينك الشحن");
    } catch {
      toast.error("تعذر نسخ لينك الشحن");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      disabled={busy}
      title={shippingTrackLink ? "نسخ لينك شحن Fincart" : "اللينك بيتسجل بعد إرسال الأوردر لـ Fincart"}
      className={
        compact
          ? "w-full bg-cyan-700 text-white py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition hover:bg-cyan-800 disabled:opacity-60"
          : "w-full bg-cyan-700 text-white px-3 py-3 rounded-lg hover:bg-cyan-800 transition text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-60"
      }
    >
      <Truck size={compact ? 13 : 16} />
      {busy ? "بنسخ اللينك..." : "لينك الشحن"}
    </button>
  );
};

export default CopyTrackingLinkButton;
